import { Delete } from "@mui/icons-material";
import AccessAlarmIcon from "@mui/icons-material/AccessAlarm";
import { Box, Button, Switch } from "@mui/material";
import { DataGridPremium } from "@mui/x-data-grid-premium";
import { EuroField, LocalizedNumberField } from "@repo/shared/components";
import {
	useAddServicesToProjectMutation,
	useRemoveServicesFromProjectMutation,
} from "@repo/server-api";
import { useNotification } from "@repo/shared/components";
import { useTranslation } from "next-i18next";
import React, { useEffect, useMemo, useRef, useState } from "react";

import {
	DialogSelectServices,
	type DialogSelectServicesHandle,
} from "../dialogs/DialogSelectServices";

import type { Project } from "../types/ProjectTypes";
import type { SelectionDiff } from "../types/ServiceTreeTypes";
import type { GridCellParams, GridColDef } from "@mui/x-data-grid";
import type { ProjectSaleableService, ProjectSaleableServiceGroup } from "@repo/server-api";

export default function ProjectServiceSection(
	props: Readonly<{
		project: Project | null;
		setProject: (project: Project) => void;
	}>,
) {
	const { t } = useTranslation();
	const { showNotification } = useNotification();
	const DialogSelectServicesHandleRef = useRef<DialogSelectServicesHandle | null>(null);

	const [services, setServices] = useState<
		(ProjectSaleableService | ProjectSaleableServiceGroup)[]
	>([]);

	const [addServices] = useAddServicesToProjectMutation({
		onCompleted(data) {
			if (data.addServicesToProject.project) {
				props.setProject({
					...props.project!,
					saleableServiceGroups: data.addServicesToProject.project.saleableServiceGroups || [],
					saleableServices: data.addServicesToProject.project.saleableServices || [],
				});
				showNotification(t("services-added-successfully"), "success");
			}
			// Error handling
			if (data.addServicesToProject.errors) {
				data.addServicesToProject.errors.forEach((error) => {
					showNotification(error.message, "error");
				});
			}
		},
		onError(error) {
			showNotification(error.message, "error");
		},
	});

	const handleServicesSaved = async (diff: SelectionDiff) => {
		const { addedServiceIds, removedServiceIds } = diff;

		// Execute remove first
		if (removedServiceIds.length > 0) {
			await removeServices({
				variables: {
					projectId: props.project?.id ?? "",
					serviceIds: removedServiceIds,
				},
			});
		}

		// Then execute add
		if (addedServiceIds.length > 0) {
			await addServices({
				variables: {
					projectId: props.project?.id ?? "",
					serviceIds: addedServiceIds,
				},
			});
		}
	};

	useEffect(() => {
		if (props.project) {
			const tmp: (ProjectSaleableService | ProjectSaleableServiceGroup)[] = [];

			props.project.saleableServiceGroups?.forEach((service: ProjectSaleableServiceGroup) => {
				if ("services" in service && service.services.length) {
					tmp.push(service);
					service.services.forEach((s: ProjectSaleableService) => {
						tmp.push(s);
					});
				}
			});

			setServices(tmp);
		}
	}, [props.project]);

	const [removeServices] = useRemoveServicesFromProjectMutation();

	const columns: GridColDef<ProjectSaleableService | ProjectSaleableServiceGroup>[] = useMemo(
		() => [
			{
				field: "name",
				headerName: t("listing"),
				width: 180,
				sortable: false,
				renderCell(params: GridCellParams) {
					return params.row.name;
				},
			},
			{
				field: "unit",
				headerName: t("unit"),
				width: 100,
				sortable: false,
				renderCell(params: GridCellParams) {
					return params.row.unit ? params.row.unit.name : "";
				},
			},
			{
				field: "hasAmountLimit",
				headerName: t("has-budget-limit"),
				width: 130,
				sortable: false,
				renderCell(params: GridCellParams) {
					return (
						<Switch
							onChange={(event) => {
								const tmp = [...services];
								const index = tmp.findIndex((service) => service.id === params.row.id);

								if (!tmp[index]) return;

								tmp[index].budget.hasAmountLimit = event.target.checked;

								if (!("services" in tmp[index])) {
									tmp.forEach((service) => {
										if (!("services" in service)) return;
										const i = service.services.findIndex((s) => s.id === params.row.id);
										if (i >= 0) {
											service.services[i].budget.hasAmountLimit = event.target.checked;
										}
									});
								}

								setServices(tmp);
							}}
							checked={params.row.budget.hasAmountLimit}
						/>
					);
				},
			},
			{
				field: "amount",
				headerName: `${t("amount")} (${t("editable-if-has-limit")})`,
				width: 180,
				sortable: false,
				renderCell(params: GridCellParams) {
					if (!params.row.budget?.hasAmountLimit) {
						return "";
					}
					return (
						<EuroField
							value={params.row.budget?.amount ?? null}
							onValueChange={(value) => {
								const tmp = [...services];
								const index = tmp.findIndex((service) => service.id === params.row.id);

								if (!tmp[index]) return;

								tmp[index].budget.amount = value || 0;

								if (!("services" in tmp[index])) {
									tmp.forEach((service) => {
										if (!("services" in service)) return;
										const i = service.services.findIndex((s) => s.id === params.row.id);
										if (i >= 0) {
											service.services[i].budget.amount = value || 0;
										}
									});
								}

								setServices(tmp);
							}}
							disabled={!params.row.budget?.hasAmountLimit}
							variant="standard"
						/>
					);
				},
			},
			{
				field: "hasTimeLimit",
				headerName: t("has-time-limit"),
				width: 130,
				sortable: false,
				renderCell(params: GridCellParams) {
					return (
						<Switch
							onChange={(event) => {
								const tmp = [...services];
								const index = tmp.findIndex((service) => service.id === params.row.id);

								if (!tmp[index]) return;

								tmp[index].budget.hasTimeLimit = event.target.checked;

								if (!("services" in tmp[index])) {
									tmp.forEach((service) => {
										if (!("services" in service)) return;
										const i = service.services.findIndex((s) => s.id === params.row.id);
										if (i >= 0) {
											service.services[i].budget.hasTimeLimit = event.target.checked;
										}
									});
								}

								setServices(tmp);
							}}
							checked={params.row.budget.hasTimeLimit}
						/>
					);
				},
			},
			{
				field: "hours",
				headerName: `${t("hours")} (${t("editable-if-has-limit")})`,
				width: 180,
				sortable: false,
				renderCell(params: GridCellParams) {
					if (!params.row.budget?.hasTimeLimit) {
						return "";
					}
					return (
						<LocalizedNumberField
							value={params.row.budget?.hours ?? null}
							onValueChange={(value) => {
								const tmp = [...services];
								const index = tmp.findIndex((service) => service.id === params.row.id);

								if (!tmp[index]) return;

								tmp[index].budget.hours = value || 0;

								if (!("services" in tmp[index])) {
									tmp.forEach((service) => {
										if (!("services" in service)) return;
										const i = service.services.findIndex((s) => s.id === params.row.id);
										if (i >= 0) {
											service.services[i].budget.hours = value || 0;
										}
									});
								}

								setServices(tmp);
							}}
							icon={<AccessAlarmIcon />}
							iconBackgroundColor="#1a455c"
							disabled={!params.row.budget?.hasTimeLimit}
							variant="standard"
						/>
					);
				},
			},
			{
				field: "hasFixSellingPrice",
				headerName: t("has-fix-selling-price"),
				width: 150,
				sortable: false,
				renderCell(params: GridCellParams) {
					return (
						<Switch
							onChange={(event) => {
								const tmp = [...services];
								const index = tmp.findIndex((service) => service.id === params.row.id);
								if (tmp[index]) {
									tmp[index].hasFixSellingPrice = event.target.checked;
								}
								setServices(tmp);
							}}
							checked={params.row.hasFixSellingPrice ?? false}
						/>
					);
				},
			},
			{
				field: "sellingPrice",
				headerName: `${t("selling-price")} (${t("editable-if-has-fix-price")})`,
				width: 180,
				sortable: false,
				renderCell(params: GridCellParams) {
					if (!params.row.hasFixSellingPrice) {
						return "";
					}
					return (
						<EuroField
							value={params.row.sellingPrice ?? null}
							onValueChange={(value) => {
								const tmp = [...services];
								const index = tmp.findIndex((service) => service.id === params.row.id);
								if (tmp[index]) {
									tmp[index].sellingPrice = value || 0;
								}
								setServices(tmp);
							}}
							variant="standard"
						/>
					);
				},
			},
			{
				field: "actions",
				headerName: t("actions"),
				width: 100,
				sortable: false,
				renderCell(params: GridCellParams) {
					return (
						<Button
							onClick={async () => {
								const tmp = [...services];
								const index = tmp.findIndex((service) => service.id === params.row.id);

								if (!tmp[index]) return;

								await removeServices({
									variables: {
										projectId: props.project?.id || "",
										serviceIds: [params.row.originalId],
									},
								});

								if ("services" in tmp[index]) {
									tmp[index].services.forEach((service) => {
										const i = tmp.findIndex((s) => s.id === service.id);
										tmp.splice(i, 1);
									});
								} else {
									tmp.forEach((service) => {
										if ("services" in service) {
											const i = service.services.findIndex((s) => s.id === params.row.id);
											if (i >= 0) {
												service.services.splice(i, 1);
											}
										}
									});
								}
								tmp.splice(index, 1);
								setServices(tmp);
							}}>
							<Delete />
						</Button>
					);
				},
			},
		],
		[t, services, props.project?.id, removeServices],
	);

	return (
		<Box sx={{ height: "50vh", width: "100%" }}>
			<Box className="clear">
				<Button
					variant="contained"
					className="greenButton"
					onClick={() => {
						if (DialogSelectServicesHandleRef.current && props.project != null) {
							DialogSelectServicesHandleRef.current.show({
								project: props.project,
								setProject: props.setProject,
							});
						}
					}}>
					{t("add-service")}
				</Button>
			</Box>
			<DialogSelectServices ref={DialogSelectServicesHandleRef} onSaved={handleServicesSaved} />
			{services.length ? (
				<Box
					sx={{
						height: "100%",
						width: "100%",
						minHeight: "370px",
						borderBottom: "1px solid lightgrey",
					}}>
					<DataGridPremium
						getRowId={(row: ProjectSaleableService | ProjectSaleableServiceGroup) =>
							row.id ? row.id : row.originalId
						}
						rows={services ? services : []}
						columns={
							columns as GridCellParams<ProjectSaleableService | ProjectSaleableServiceGroup>[]
						}
						disableRowSelectionOnClick
						className="table"
						getCellClassName={(params) => {
							if ("services" in params.row) {
								return "greyBackground";
							}
							return "";
						}}
						hideFooter
					/>
				</Box>
			) : (
				""
			)}
		</Box>
	);
}
